/**
 * Utility functions for form validation and formatting.
 * 
 */
import { useDateFormat } from "@vueuse/core";
/**
 * Retrieves the validation message for a specific property from the errors array.
 * 
 * @param {Array} errors - The array of errors.
 * @param {string} property - The property to retrieve the validation message for.
 * @returns {string} - The validation message for the specified property.
 */
export const validationMessage = (errors, property) => {
    const { $message } = (errors.find(p => p.$propertyPath === property) || {});
    return $message ?? '';
};

/**
 * Formats a phone number in the format (XXX) XXX-XXXX.
 * 
 * @param {string} phoneNumber - The phone number to format.
 * @returns {string} - The formatted phone number.
 */
export const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
};

/**
 * Formats a postal code by inserting a hyphen between the first 5 digits and the next 4 digits.
 * If the input is null, undefined, or an empty string, it returns an empty string.
 *
 * @param {string} postalCode - The postal code to format. Expected to be a string of 9 digits.
 * @returns {string} The formatted postal code in the format "XXXXX-XXXX", or an empty string if the input is invalid.
 */
export const formatPostalCode = (postalCode) => {
    if (!postalCode) return '';
    return postalCode.replace(/(\d{5})(\d{4})/, '$1-$2');
};

/**
 * Formats a given date into the 'MM/DD/YYYY' format.
 *
 * @param {Date|string|null} date - The date to format. Can be a Date object, a string, or null/undefined.
 * @returns {string} The formatted date as a string, or an empty string if the input is null/undefined.
 */
export const formatDate = (date) => {
    if (!date) return '';
    return useDateFormat(date, 'MM/DD/YYYY');
};

/**
 * Sanitizes a given input by removing all non-numeric characters.
 *
 * @param {string} value - The input string to sanitize.
 * @returns {string} A string containing only numeric characters. Returns an empty string if the input is falsy.
 */
export const sanitizeNumber = (value) => {
    if (!value) return '';
    return value.replace(/\D/g, '');
};

/**
 * Removes all spaces from the given string.
 *
 * @param {string} value - The input string from which spaces will be removed.
 * @returns {string} The input string with all spaces removed. If the input is falsy, returns an empty string.
 */
export const removeSpaces = (value) => {
    if (!value) return '';
    return value.replace(/\s/g, '');
};

/**
 * Replaces all occurrences of the word "SAINT" (case-insensitive) in the given string with "ST.".
 * If the input value is falsy, it returns an empty string.
 *
 * @param {string} value - The input string to process.
 * @returns {string} The modified string with "SAINT" replaced by "ST." or an empty string if the input is falsy.
 */
export function replaceSaintWithSt(value) {
    if (!value) return '';
    return value.replace(/SAINT/gi, 'ST.');
};

